import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { FreemiumContext } from '../Freemium/context';
import { ModularListingContext } from '../../containers/CreateListingPage/context';

import accessUtils from '../../utils/accessUtils';
import { getSubStepMap, hasSubStep } from '../../utils/wizardUtils';

import { STEP_DIRECTION } from './constants';

import ProgressStepper from '../Stepper/ProgressStepper';
import Step from '../Stepper/Step';

import { EXIT_CTA_LABEL } from '../../constants/WizardConstants';

import { StyledProgressStepperContainer, StyledProgressStepperContent } from './StyledComponents';
import { logError } from '@zg-rentals/log-error';
import { useSelector } from '../../selectors/useSelector';

import type React from 'react';
import type { WizardStepType } from '../Wizard/types';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

type WizardStepControllerProps = {
  currentStepKey: string;
  isOrdered?: boolean;
  onGoToStep: (stepKey: string, options?: { stepDirection?: string }) => void;
  progressSteps?: Array<{ key: string; label: string }>;
  steps: Array<WizardStepType>;
  propertyId?: string;
  wizardTitle?: React.ReactNode;
  offsetStartIndex?: number;
  onExitWizard?: () => void;
  progressBarLeft?: number;
  progressBarRight?: number;
  showProgressStepperDesktopViewBreakpoint?: Breakpoint;
  showPageWizardDesktopViewBreakpoint?: Breakpoint;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isWizardComplete?: boolean;
  entryPoint?: string;
  exitCTALabel?: string;
  onMissingWizardBodyTemplate?: () => void;
};

const WizardStepController = ({
  currentStepKey,
  isOrdered = true,
  onGoToStep,
  progressSteps = [],
  steps,
  propertyId,
  wizardTitle,
  onExitWizard,
  offsetStartIndex = 0,
  progressBarLeft = -50,
  progressBarRight = 48,
  showProgressStepperDesktopViewBreakpoint = 'md',
  showPageWizardDesktopViewBreakpoint = 'md',
  isBackDisabled = false,
  isNextDisabled = false,
  isWizardComplete = false,
  entryPoint = '',
  exitCTALabel = EXIT_CTA_LABEL.EXIT,
  onMissingWizardBodyTemplate = () => {},
}: WizardStepControllerProps) => {
  const {
    freemiumState: { canAccessTopOfFunnelPremium },
  } = useContext(FreemiumContext);
  const {
    modularListingState: { isInReactivationFlow },
  } = useContext(ModularListingContext);

  const { active, canBeRelisted, contactEmail, userEmail } = useSelector(
    (state) => ({
      active: state.properties.currentProperty.active,
      canBeRelisted: state.properties.currentProperty.canBeRelisted,
      contactEmail: state.properties.currentProperty.contactEmail,
      userEmail: state.user.email,
    }),
    shallowEqual,
  );

  useEffect(() => {
    // allow time for CONFIG to update
    setTimeout(() => {
      checkForSubSteps();
    }, 0);
  }, [steps.length]);

  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
    canBeRelisted,
  });

  const [hasSubSteps, setHasSubSteps] = useState(false);
  const [subStepMap, setSubStepMap] = useState<Map<string, Array<WizardStepType>>>();

  const shouldRenderProgressStepper = useMemo(() => {
    if (!isInReactivationFlow) {
      return true;
    } else if (canAccessRelistingFlow) {
      return true;
    }

    return false;
  }, [active, isInReactivationFlow, canAccessRelistingFlow]);

  // TODO: refactor to DRY variables after errors subside
  const firstValidProgressStepKey = progressSteps[0]?.key;
  const firstValidProgressStepIndex = steps.findIndex((step) => step.progressStepKey === firstValidProgressStepKey);

  const firstValidStepKey =
    firstValidProgressStepIndex >= 0 ? steps[firstValidProgressStepIndex]?.stepKey : steps[0]?.stepKey;

  const currentIndex = useMemo(() => {
    const currentStepIndex = steps.findIndex((step) => step.stepKey === currentStepKey);

    return currentStepIndex !== -1 ? currentStepIndex : steps.findIndex((step) => step.stepKey === firstValidStepKey);
  }, [currentStepKey, firstValidStepKey, steps?.length]);

  const currentStep = steps[currentIndex];
  const currentProgressStepKey = currentStep && currentStep.progressStepKey;
  const progressStepIndex = progressSteps.findIndex((progressStep) => progressStep.key === currentProgressStepKey);
  const currentProgressStepLabel = progressSteps[progressStepIndex]?.label;

  const progressSubStepList = subStepMap?.get(currentProgressStepKey) || [];
  const stepList = hasSubSteps ? progressSubStepList : steps;
  const foundStepIndex = stepList.findIndex((step) => step.stepKey === currentStepKey) + 1;
  const currentStepIndex = hasSubSteps ? foundStepIndex : foundStepIndex - offsetStartIndex;

  // TODO: remove error log after after errors subside
  if (firstValidStepKey === undefined) {
    logError({
      error: new Error(
        `ERROR@WizardStepController: Attempted to render currentStep: ${currentStep} with currentStepKey: ${currentStepKey} as input.  Redirecting to ${firstValidStepKey}.`,
      ),
      errorType: 'WizardStepControllerError',
      context: {
        steps,
        details: `WizardStepController. Emails are contactEmail: ${contactEmail}, userEmail: ${userEmail}. ${
          userEmail?.toLowerCase() === contactEmail?.toLowerCase() ? 'SAME EMAIL' : 'EMAIL MISMATCH'
        }.`,
      },
    });
  }

  const checkForSubSteps = () => {
    if (hasSubStep(steps)) {
      setHasSubSteps(true);
      setSubStepMap(getSubStepMap(steps));
    }
  };

  const renderCurrentComponent = () => {
    const CurrentWizardBodyTemplate = currentStep && currentStep.wizardBodyTemplate;
    const totalSteps = hasSubSteps ? progressSubStepList.length : steps.length;

    let { nextStepKey } = currentStep || {};
    let { previousStepKey } = currentStep || {};

    if (!CurrentWizardBodyTemplate) {
      if (onMissingWizardBodyTemplate) {
        logError({
          error: new Error(
            `Undefined CurrentWizardBodyTemplate. Attempted to render currentStep: ${currentStep} with currentStepKey: ${currentStepKey} as input.  Handling redirect via onMissingWizardBodyTemplate handler.`,
          ),
          errorType: 'WizardStepControllerError',
          context: {
            steps,
            details: 'WizardStepController.renderCurrentComponent.onMissingWizardBodyTemplate',
          },
        });

        onMissingWizardBodyTemplate();

        return <></>;
      }

      // fallback to render the first valid step within the progress step list
      const firstValidProgressStepKey = progressSteps[0].key;
      const firstValidProgressStepIndex = steps.findIndex((step) => step.progressStepKey === firstValidProgressStepKey);
      const firstValidStepKey =
        firstValidProgressStepIndex >= 0 ? steps[firstValidProgressStepIndex]?.stepKey : steps[0]?.stepKey;

      logError({
        error: new Error(
          `ERROR@WizardStepController: Undefined CurrentWizardBodyTemplate.  Attempted to render currentStep: ${currentStep} with currentStepKey: ${currentStepKey} as input.  Redirecting to ${firstValidStepKey}.`,
        ),
        errorType: 'WizardStepControllerError',
        context: {
          steps,
          details: `WizardStepController.renderCurrentComponent. Emails are contactEmail: ${contactEmail}, userEmail: ${userEmail}. ${
            userEmail?.toLowerCase() === contactEmail?.toLowerCase() ? 'SAME EMAIL' : 'EMAIL MISMATCH'
          }.`,
        },
      });

      onGoToStep(firstValidStepKey);

      return <></>;
    }

    if (isOrdered) {
      const nextStep = steps[currentIndex + 1];
      nextStepKey = nextStep && nextStep.stepKey;
      const previousStep = steps[currentIndex - 1];
      previousStepKey = previousStep && previousStep.stepKey;
    }

    const progressStepperToRender = shouldRenderProgressStepper
      ? progressSteps.length &&
        !currentStep.hideProgressStepper && (
          <StyledProgressStepperContainer>
            <StyledProgressStepperContent>
              <ProgressStepper
                activeIndex={progressStepIndex}
                currentProgressStepLabel={currentProgressStepLabel}
                currentStepIndex={currentStepIndex}
                totalSteps={totalSteps}
                left={progressBarLeft}
                right={progressBarRight}
                showDesktopViewBreakpoint={showProgressStepperDesktopViewBreakpoint}
                isWizardComplete={isWizardComplete}
              >
                {progressSteps.map((progressStep) => (
                  // @ts-expect-error
                  <Step key={progressStep.key} label={progressStep.label} />
                ))}
              </ProgressStepper>
            </StyledProgressStepperContent>
          </StyledProgressStepperContainer>
        )
      : '';

    return (
      <CurrentWizardBodyTemplate
        entryPoint={entryPoint}
        exitCTALabel={exitCTALabel}
        onGoToStep={onGoToStep}
        onGoToNextStep={nextStepKey ? () => onGoToStep(nextStepKey, { stepDirection: STEP_DIRECTION.NEXT }) : null}
        onGoToPrevStep={
          previousStepKey ? () => onGoToStep(previousStepKey, { stepDirection: STEP_DIRECTION.PREVIOUS }) : null
        }
        hasPrevStep={!!previousStepKey}
        hasNextStep={!!nextStepKey}
        propertyId={propertyId}
        wizardTitle={wizardTitle}
        totalSteps={totalSteps}
        currentStepIndex={currentStepIndex}
        onExitWizard={onExitWizard}
        showDesktopViewBreakpoint={showPageWizardDesktopViewBreakpoint}
        displayPremiumTopOfFunnelHeader={canAccessTopOfFunnelPremium}
        progressStepper={progressStepperToRender}
        currentStepKey={currentStepKey}
        isBackDisabled={isBackDisabled}
        isNextDisabled={isNextDisabled}
      />
    );
  };

  return <>{renderCurrentComponent()}</>;
};

export default WizardStepController;
